"use client";

import type { MessageOrder } from "../../../constants";
import { UiMessageBubbleAvatar } from "./UiMessageBubbleAvatar";
import { UiMessageBubbleName } from "./UiMessageBubbleName";
import { UiMessageBubbleStatus } from "./UiMessageBubbleStatus";
import { UiMessageDeliveredStatus } from "./UiMessageDeliveredStatus";
import {
  UiMessageBubbleStore,
  UiMessageBubbleStoreContext,
} from "./_sotre/UiMessageBubbleStore";
import { receivedBubbleStyles, sentBubbleStyles } from "./styles";
import {
  MessagingEventType,
  MessagingMessageType,
  MessagingStatus,
  type MessagingMessage,
} from "@/model/messaging";
import { cn, timestampToString } from "@deputy-ai/common";
import { useAtomsFromProps, useAtomValues } from "@deputy-ai/common/jotai";
import { useCallbackRef } from "@radix-ui/react-use-callback-ref";
import { useCreation } from "ahooks";
import { format } from "date-fns";
import { type ComponentProps, useEffect, useState } from "react";

export type UiMessageBubblePosition = "sent" | "received";

export interface UiMessageBubbleCallbacks {
  onResendMessage?: (message: MessagingMessage) => void;
  onIntersect?: (entry: IntersectionObserverEntry) => void;
}

export interface UiMessageBubbleDataProps {
  containerElement?: HTMLDivElement;
  message: MessagingMessage;
  participantName?: string;
  nameVisible?: boolean | null;
  avatar?: string;
  avatarVisible?: boolean | null;
  timeBarVisible?: boolean;
  deliveredStatusVisible?: boolean;
  position: UiMessageBubblePosition;
  order: MessageOrder;
  bubbleStyles?: {
    sent?: string;
    received?: string;
  };
  maxWidth?: number;
  isRead?: boolean;
  isDelivered?: boolean;
}

export type UiMessageBubbleProps = ComponentProps<"div"> &
  UiMessageBubbleDataProps &
  UiMessageBubbleCallbacks &
  BubbleProps;

export function UiMessageBubble(props: UiMessageBubbleProps) {
  const {
    containerElement,
    message,
    participantName,
    nameVisible,
    avatar,
    avatarVisible,
    timeBarVisible,
    deliveredStatusVisible,
    position,
    order,
    bubbleStyles,
    maxWidth,
    isRead,
    isDelivered,
    // callbacks
    onResendMessage,
    onIntersect,
    ...restProps
  } = props;

  const propsAtoms = useAtomsFromProps({
    containerElement,
    message,
    participantName,
    nameVisible,
    avatar,
    avatarVisible,
    timeBarVisible,
    deliveredStatusVisible,
    position,
    order,
    bubbleStyles,
    maxWidth,
    isRead,
    isDelivered,
  });

  const callbacks = {
    onResendMessage: useCallbackRef(onResendMessage),
    onIntersect: useCallbackRef(onIntersect),
  };

  const store = useCreation(() => {
    return new UiMessageBubbleStore({
      propsAtoms,
      callbacks,
    });
  }, []);

  return (
    <UiMessageBubbleStoreContext.Provider store={store}>
      <Bubble {...restProps} />
    </UiMessageBubbleStoreContext.Provider>
  );
}

export type BubbleProps = ComponentProps<"div"> & {
  children?: React.ReactNode;
  footer?: React.ReactNode;
};

function Bubble(props: BubbleProps) {
  const { children, footer, className, ...restProps } = props;

  const {
    propsAtoms,
    callbacks: { onIntersect },
  } = UiMessageBubbleStoreContext.useStore();

  const {
    containerElement,
    timeBarVisible,
    message,
    position,
    order,
    bubbleStyles,
    deliveredStatusVisible,
    maxWidth,
  } = useAtomValues(propsAtoms);

  const [el, setEl] = useState<HTMLDivElement | null>(null);

  const styles = position === "sent" ? sentBubbleStyles : receivedBubbleStyles;

  // handle intersect
  useEffect(() => {
    if (!containerElement || !el) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onIntersect?.(entry);
        }
      },
      {
        root: containerElement,
        threshold: 0.6,
      }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [containerElement, el]);

  if (!message) {
    return null;
  }

  if (message.eventType === MessagingEventType.Debug) {
    return (
      <div className="overflow-x-auto border text-xs text-gray-500">
        <div className="bg-gray-100 p-2">DEBUG</div>
        <div className="px-4 py-2">{children}</div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)} {...restProps}>
      {timeBarVisible && <TimeBar />}
      <div
        className={cn(
          "flex flex-row gap-2",
          position === "sent" && "flex-row-reverse"
        )}
      >
        <UiMessageBubbleAvatar />
        <div
          ref={(el) => {
            setEl(el);
          }}
          className={cn(
            "group/message-bubble relative flex flex-col gap-1",
            position === "sent" && "items-end"
          )}
        >
          <UiMessageBubbleName />
          <UiMessageBubbleStatus />
          {message.status !== MessagingStatus.Failed && (
            <EventTime className="hidden group-hover/message-bubble:block" />
          )}
          <div
            className={cn(
              styles.container,
              message.payload.messageType !== MessagingMessageType.Form &&
                "px-4 py-2.5",
              styles[order],
              position === "received"
                ? bubbleStyles?.received
                : bubbleStyles?.sent
            )}
            style={{
              maxWidth: `${(maxWidth ?? 800) - 60}px`,
            }}
          >
            {children}
          </div>
          {deliveredStatusVisible && <UiMessageDeliveredStatus />}
          {footer}
        </div>
      </div>
    </div>
  );
}

function EventTime(props: ComponentProps<"span">) {
  const { className, ...restProps } = props;

  const { propsAtoms } = UiMessageBubbleStoreContext.useStore();

  const { message, position } = useAtomValues(propsAtoms);

  const styles =
    position === "sent"
      ? sentBubbleStyles.exactTime
      : receivedBubbleStyles.exactTime;

  return (
    <span className={cn(styles, className)} {...restProps}>
      {format(message.eventTime, "HH:mm")}
    </span>
  );
}

const TimeBar = () => {
  const { propsAtoms } = UiMessageBubbleStoreContext.useStore();

  const { message } = useAtomValues(propsAtoms);

  return (
    <div className="py-1 text-center text-sm text-gray-500">
      {timestampToString(message.eventTime)}
    </div>
  );
};
